// src/app/admin/admin-forms/services/data-catalog.service.ts
//
// Servicio para gestionar catálogos de datos (R5).
// Un catálogo es una colección reutilizable de opciones que puede
// ser referenciada por cualquier campo con catalogId.
//
// Los catálogos se almacenan en la tabla `form_catalogs` de Postgres.
// En tiempo de ejecución del formulario, el DynamicFormComponent
// reemplaza las opciones inline del campo con las del catálogo.
//
// Endpoint base: environment.URL_SERVER + /api/admin/form-catalogs

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, shareReplay, tap } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { FieldOption }  from '../models/field-config.model';

// ─── Modelos de catálogo ──────────────────────────────────────────────────────

export interface CatalogItem extends FieldOption {
  /** Valor de la opción (string, number o boolean) */
  value:     string | number | boolean;
  /** Etiqueta mostrada al usuario */
  label:     string;
  /** Indica si esta opción está deshabilitada */
  disabled?: boolean;
  /** Grupo para agrupación visual (optgroup) */
  group?:    string;
  /** Metadatos adicionales libres */
  meta?:     Record<string, unknown>;
}

export interface DataCatalog {
  id:          string;
  name:        string;
  description?: string;
  items:       CatalogItem[];
  createdAt?:  string;
  updatedAt?:  string;
}

export interface DataCatalogListItem {
  id:          string;
  name:        string;
  description?: string;
  itemsCount:   number;
  updatedAt?:  string;
}

export interface DataCatalogListResponse {
  items: DataCatalogListItem[];
  total: number;
}

// ─── Servicio ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class DataCatalogService {

  private readonly BASE        = `${environment.URL_SERVER}/api/admin/form-catalogs`;
  private readonly PUBLIC_BASE = `${environment.URL_SERVER}/api/public/form-catalogs`;

  private http = inject(HttpClient);

  /** Cache en memoria: id → DataCatalog. Evita re-fetching en el mismo builder. */
  private cache = new Map<string, DataCatalog>();

  // ── Listar ────────────────────────────────────────────────────────────────

  /** Lista todos los catálogos disponibles (resumen sin items). */
  getAll(search?: string): Observable<DataCatalogListResponse> {
    let params = new HttpParams();
    if (search) params = params.set('search', search);
    return this.http.get<DataCatalogListResponse>(this.BASE, { params });
  }

  // ── Obtener ────────────────────────────────────────────────────────────────

  /**
   * Retorna un catálogo completo con sus items.
   * Si ya está en caché, retorna la copia en memoria.
   */
  getById(id: string): Observable<DataCatalog> {
    if (this.cache.has(id)) {
      return of(this.cache.get(id)!);
    }
    return this.http.get<DataCatalog>(`${this.BASE}/${id}`).pipe(
      tap(catalog => this.cache.set(id, catalog))
    );
  }

  /**
   * Retorna solo los items de un catálogo para poblar opciones de un campo.
   * Usa la caché si está disponible.
   * Llama al endpoint público GET /api/public/form-catalogs/:id/items
   * para que funcione tanto en formularios públicos (sin sesión) como en el builder.
   */
  getItems(catalogId: string): Observable<CatalogItem[]> {
    if (this.cache.has(catalogId)) {
      return of(this.cache.get(catalogId)!.items);
    }
    return this.http
      .get<CatalogItem[]>(`${this.PUBLIC_BASE}/${catalogId}/items`)
      .pipe(shareReplay(1));
  }

  // ── Crear ──────────────────────────────────────────────────────────────────

  /** Crea un nuevo catálogo vacío o con items iniciales. */
  create(catalog: Omit<DataCatalog, 'id' | 'createdAt' | 'updatedAt'>): Observable<DataCatalog> {
    return this.http.post<DataCatalog>(this.BASE, catalog).pipe(
      tap(saved => this.cache.set(saved.id, saved))
    );
  }

  // ── Actualizar ────────────────────────────────────────────────────────────

  /** Actualiza un catálogo (nombre, descripción e items). */
  update(id: string, partial: Partial<DataCatalog>): Observable<DataCatalog> {
    return this.http.put<DataCatalog>(`${this.BASE}/${id}`, partial).pipe(
      tap(saved => this.cache.set(saved.id, saved))
    );
  }

  /**
   * Actualiza solo los items de un catálogo.
   * Usa PUT /api/admin/form-catalogs/:id enviando únicamente los items;
   * el backend acepta campos opcionales y hace merge del resto.
   */
  updateItems(id: string, items: CatalogItem[]): Observable<DataCatalog> {
    return this.http.put<DataCatalog>(`${this.BASE}/${id}`, { items }).pipe(
      tap(saved => this.cache.set(saved.id, saved))
    );
  }

  // ── Eliminar ──────────────────────────────────────────────────────────────

  /** Elimina un catálogo permanentemente. */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.BASE}/${id}`).pipe(
      tap(() => this.cache.delete(id))
    );
  }

  // ── Cache helpers ─────────────────────────────────────────────────────────

  /** Invalida la entrada de caché para forzar re-fetch. */
  invalidate(id: string): void {
    this.cache.delete(id);
  }

  /** Limpia toda la caché. */
  clearCache(): void {
    this.cache.clear();
  }
}
